import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { formatDecompositionResponse } from '../../../../../../helpers/utils/formatDecompositionResponse';
import useCheckDecomposition from '../../../../../../hooks/products/useCheckDecomposition';
import useCreateDecomposition from '../../../../../../hooks/products/useCreateDecomposition';
import useReserveDecomposition from '../../../../../../hooks/products/useReserveDecomposition';

const SavDecompositionCheck = forwardRef(({ lines, origin, createdBy, onCheckDone, onDecompositionApplied, onAllResolved, locked = false }, ref) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [decompositionUi, setDecompositionUi] = useState(null);
  const [creationStates, setCreationStates] = useState({});
  const [errorMessages, setErrorMessages] = useState({});
  const [reservationStates, setReservationStates] = useState({});
  const [reservationErrors, setReservationErrors] = useState({});
  const allResolvedFiredRef = useRef(false);
  const { checkDecomposition } = useCheckDecomposition();
  const { createDecomposition } = useCreateDecomposition();
  const { reserveDecomposition } = useReserveDecomposition();

  // Une ligne est "résolue" si elle est en stock, ou si toutes ses actions (réservation
  // et/ou décomposition) ont réussi. Une ligne bloquée (error) n'est jamais résolue.
  const isRowResolved = (row) => {
    if (row.variant === 'error') return false;
    if (row.inStock) return true;
    let resolved = true;
    if (row.pendingDecomposition) {
      resolved = resolved && reservationStates[row.reference] === 'success';
    }
    if (row.candidate) {
      resolved = resolved && creationStates[row.candidate] === 'success';
    }
    if (!row.pendingDecomposition && !row.candidate) {
      resolved = resolved && Boolean(row.successMessage);
    }
    return resolved;
  };

  // Quand toutes les lignes sont résolues ET qu'au moins une réservation/décomposition a
  // été appliquée (la SAV est donc en attente de décomposition côté BO), on notifie le
  // parent pour finaliser le brouillon. On ne déclenche jamais pour un cas 100% en stock.
  useEffect(() => {
    if (!decompositionUi || decompositionUi.serverError) return;
    if (allResolvedFiredRef.current) return;
    const rows = decompositionUi.pcsRows;
    if (!rows.length) return;
    const hasAppliedAction =
      Object.values(reservationStates).includes('success') ||
      Object.values(creationStates).includes('success');
    if (!hasAppliedAction) return;
    const allResolved =
      rows.every(isRowResolved) &&
      decompositionUi.commonDecompositions.every(
        (g) => creationStates[g.internalProductRef] === 'success'
      );
    if (allResolved) {
      allResolvedFiredRef.current = true;
      onAllResolved?.();
    }
  }, [decompositionUi, reservationStates, creationStates]);

  useImperativeHandle(ref, () => ({
    createAllPending: async () => {
      if (!decompositionUi) return;

      const candidates = decompositionUi.pcsRows
        .filter(
          (row) =>
            row.candidate &&
            row.variant !== 'error' &&
            !decompositionUi.commonDecompositions.some(
              (g) => g.internalProductRef === row.candidate
            ) &&
            creationStates[row.candidate] !== 'success'
        )
        .map((row) => ({
          reference: row.candidate,
          quantity: row.decompositionUnits ?? 1,
        }));

      const commons = decompositionUi.commonDecompositions
        .filter((g) => creationStates[g.internalProductRef] !== 'success')
        .map((g) => ({ reference: g.internalProductRef, quantity: g.quantity }));

      const pendingReservations = decompositionUi.pcsRows
        .filter(
          (row) =>
            row.pendingDecomposition &&
            reservationStates[row.reference] !== 'success'
        )
        .map((row) => ({
          reference: row.reference,
          quantity: row.pendingDecomposition.quantity || 1,
        }));

      await Promise.all([
        ...[...candidates, ...commons].map(({ reference, quantity }) =>
          handleCreateDecomposition(reference, quantity)
        ),
        ...pendingReservations.map(({ reference, quantity }) =>
          handleReserveDecomposition(reference, quantity)
        ),
      ]);
    },
  }), [decompositionUi, creationStates, reservationStates]);

  const buildLinesPayload = () =>
    lines
      .filter((line) => line.isValid !== false)
      .map((line) => ({
        reference: line.internalProduct || line.reference,
        quantity: line.quantity ?? 1,
      }));

  const hasPendingDecompositionActions = (uiState) => {
    if (!uiState || uiState.serverError) return false;

    const hasCommonDecomposition = uiState.commonDecompositions.length > 0;
    const hasRowAction = uiState.pcsRows.some((row) => {
      const hasPendingReservation = Boolean(row.pendingDecomposition);
      const hasIndividualCandidate =
        Boolean(row.candidate) &&
        !uiState.commonDecompositions.some(
          (group) => group.internalProductRef === row.candidate
        );
      return hasPendingReservation || hasIndividualCandidate;
    });

    return hasCommonDecomposition || hasRowAction;
  };

  const handleCheck = async () => {
    const linesPayload = buildLinesPayload();
    if (!linesPayload.length) return;
    const orderedRefs = linesPayload.map((item) => item.reference);
    try {
      setIsSubmitting(true);
      setDecompositionUi(null);
      setCreationStates({});
      allResolvedFiredRef.current = false;
      const response = await checkDecomposition(linesPayload);
      const uiState = formatDecompositionResponse(response, orderedRefs);
      setDecompositionUi(uiState);
      onCheckDone?.({
        checked: true,
        requiresAction: hasPendingDecompositionActions(uiState),
      });
    } catch (error) {
      console.error('Validation décomposition :', error);
      setDecompositionUi(formatDecompositionResponse(null, orderedRefs));
      onCheckDone?.({ checked: false, requiresAction: false });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateDecomposition = async (reference, quantity) => {
    setCreationStates((prev) => ({ ...prev, [reference]: 'loading' }));
    try {
      await createDecomposition({ reference, quantity, origin, createdBy });
      setCreationStates((prev) => ({ ...prev, [reference]: 'success' }));
      // La SAV d'origine passe en "attente de décomposition" côté BO : le flux est terminal.
      onDecompositionApplied?.();
    } catch (error) {
      setCreationStates((prev) => ({ ...prev, [reference]: 'error' }));
      setErrorMessages((prev) => ({ ...prev, [reference]: error.message }));
    }
  };

  const handleReserveDecomposition = async (pcsReference, quantity) => {
    setReservationStates((prev) => ({ ...prev, [pcsReference]: 'loading' }));
    try {
      await reserveDecomposition({
        savOrder: origin,
        createdBy,
        lines: [{ reference: pcsReference, quantity }],
      });
      setReservationStates((prev) => ({ ...prev, [pcsReference]: 'success' }));
      // reserve_decomposition bascule la SAV en "attente de décomposition" : le flux est terminal.
      onDecompositionApplied?.();
    } catch (error) {
      setReservationStates((prev) => ({ ...prev, [pcsReference]: 'error' }));
      setReservationErrors((prev) => ({ ...prev, [pcsReference]: error.message }));
    }
  };

  const validLines = buildLinesPayload();

  return (
    <Box>
      <Divider sx={{ mb: 2 }} />
      <Typography
        color="primary"
        sx={{ fontSize: '1em', fontWeight: 'bold', mb: 1 }}
      >
        Décomposition logistique
      </Typography>
      <Button
        variant="outlined"
        size="small"
        onClick={handleCheck}
        disabled={isSubmitting || validLines.length === 0 || locked}
        startIcon={
          isSubmitting ? <CircularProgress size={14} color="inherit" /> : null
        }
      >
        Vérifier la décomposition
      </Button>

      {decompositionUi && (
        <Box sx={{ mt: 2 }}>
          {decompositionUi.serverError ? (
            <Alert severity="error">
              Impossible d&apos;obtenir la vérification de décomposition
              (réponse vide ou erreur réseau).
            </Alert>
          ) : (
            <>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1.5 }}
              >
                {decompositionUi.summary.errorCount > 0 && (
                  <>Blocages : {decompositionUi.summary.errorCount}. </>
                )}
                {decompositionUi.summary.warnCount > 0 && (
                  <>À surveiller : {decompositionUi.summary.warnCount}. </>
                )}
                {decompositionUi.summary.pendingCount > 0 && (
                  <>
                    Décomposition en cours :{' '}
                    {decompositionUi.summary.pendingCount}.{' '}
                  </>
                )}
                {decompositionUi.summary.successCount > 0 && (
                  <>OK : {decompositionUi.summary.successCount}. </>
                )}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {decompositionUi.pcsRows.map((row) => (
                  <Alert key={row.reference} severity={row.variant}>
                    <Typography
                      variant="subtitle2"
                      component="div"
                      sx={{ mb: 0.5 }}
                    >
                      {row.reference}
                      {row.candidate ? (
                        <Chip
                          label={`Candidat : ${row.candidate}`}
                          size="small"
                          sx={{ ml: 1, verticalAlign: 'middle' }}
                          color={
                            row.variant === 'error' ? 'default' : 'primary'
                          }
                          variant="outlined"
                        />
                      ) : null}
                    </Typography>
                    {row.successMessage && (
                      <Typography variant="body2">
                        {row.successMessage}
                      </Typography>
                    )}
                    {row.errorMessage && (
                      <Typography variant="body2" color="inherit">
                        {row.errorMessage}
                      </Typography>
                    )}
                    {row.candidate &&
                      row.variant !== 'error' &&
                      !decompositionUi.commonDecompositions.some(
                        (g) => g.internalProductRef === row.candidate
                      ) && (
                        <Box sx={{ mt: 1.5 }}>
                          {creationStates[row.candidate] === 'loading' && (
                            <CircularProgress size={14} color="inherit" />
                          )}
                          {creationStates[row.candidate] === 'success' && (
                            <Chip
                              label="Ordre créé"
                              color="success"
                              size="small"
                              icon={<CheckCircleOutlineIcon />}
                            />
                          )}
                          {creationStates[row.candidate] === 'error' && (
                            <Typography variant="caption" color="error">
                              {errorMessages[row.candidate] || 'Échec de la création, veuillez réessayer.'}
                            </Typography>
                          )}
                        </Box>
                      )}
                    {row.pendingDecomposition && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          {row.pendingDecomposition.message}
                        </Typography>
                        <Box sx={{ mt: 1.5 }}>
                          {reservationStates[row.reference] === 'success' ? (
                            <Chip
                              label="Réservé sur la décomposition en cours"
                              color="info"
                              size="small"
                              icon={<CheckCircleOutlineIcon />}
                            />
                          ) : (
                            <Button
                              variant="outlined"
                              size="small"
                              color="info"
                              disabled={
                                reservationStates[row.reference] === 'loading'
                              }
                              startIcon={
                                reservationStates[row.reference] === 'loading' ? (
                                  <CircularProgress size={14} color="inherit" />
                                ) : null
                              }
                              onClick={() =>
                                handleReserveDecomposition(
                                  row.reference,
                                  row.pendingDecomposition.quantity || 1
                                )
                              }
                            >
                              Réserver sur la décomposition en cours
                            </Button>
                          )}
                          {reservationStates[row.reference] === 'error' && (
                            <Typography
                              variant="caption"
                              color="error"
                              display="block"
                              sx={{ mt: 0.5 }}
                            >
                              {reservationErrors[row.reference] ||
                                'Échec de la réservation, veuillez réessayer.'}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Alert>
                ))}
              </Box>

              {decompositionUi.commonDecompositions.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Décompositions communes (même produit interne)
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                  >
                    {decompositionUi.commonDecompositions.map((group) => (
                      <Alert
                        key={group.internalProductRef}
                        severity="info"
                        icon={false}
                      >
                        <Typography variant="subtitle2" component="div">
                          {group.internalProductRef}{' '}
                          <Chip
                            label={`${group.quantity} unité(s) à décomposer`}
                            size="small"
                            sx={{ ml: 0.5, verticalAlign: 'middle' }}
                          />
                        </Typography>
                        {group.message && (
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {group.message}
                          </Typography>
                        )}
                        {group?.pcsDetails?.length ? (
                          <Typography
                            variant="caption"
                            display="block"
                            sx={{ mt: 0.5 }}
                          >
                            {group.pcsDetails.join(' · ')}
                          </Typography>
                        ) : null}
                        <Box sx={{ mt: 1.5 }}>
                          {creationStates[group.internalProductRef] === 'loading' && (
                            <CircularProgress size={14} color="inherit" />
                          )}
                          {creationStates[group.internalProductRef] === 'success' && (
                            <Chip
                              label="Ordre créé"
                              color="success"
                              size="small"
                              icon={<CheckCircleOutlineIcon />}
                            />
                          )}
                          {creationStates[group.internalProductRef] === 'error' && (
                            <Typography variant="caption" color="error">
                              {errorMessages[group.internalProductRef] || 'Échec de la création, veuillez réessayer.'}
                            </Typography>
                          )}
                        </Box>
                      </Alert>
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
});

export default SavDecompositionCheck;
